import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  IEpisodeScript,
  ScriptStep,
  StepStatus,
  StepData,
  StorySummaryData,
  VisualStyleData,
  CharactersData,
  PropsData,
  ScenesData,
  ShotsData,
  ScriptMode,
  ScriptGenerationStatus
} from '@packages/global';
import { episodeScriptApi } from '../services/user';

/**
 * 步骤定义
 */
export const SCRIPT_STEPS = [
  { stepNumber: 1, name: '故事概要', key: 'storySummary' },
  { stepNumber: 2, name: '视觉风格', key: 'visualStyle' },
  { stepNumber: 3, name: '角色设计', key: 'characters' },
  { stepNumber: 4, name: '道具设计', key: 'props' },
  { stepNumber: 5, name: '场景设计', key: 'scenes' },
  { stepNumber: 6, name: '分镜脚本', key: 'shots' }
] as const;

/**
 * 本地备份数据
 */
interface LocalBackup {
  scriptId: string;
  stepNumber: number;
  data: Partial<StepData>;
  timestamp: number;
}

/**
 * 脚本生成状态
 */
interface ScriptGenerationState {
  // 当前脚本数据
  script: IEpisodeScript | null;
  scriptId: string | null;
  loading: boolean;
  error: string | null;

  // 步骤状态
  currentStep: number;
  steps: ScriptStep[];

  // 编辑状态
  isDirty: boolean;
  pendingChanges: Partial<StepData> | null;
  lastSavedAt: number | null;

  // 本地备份
  localBackups: LocalBackup[];

  // Actions
  loadScript: (scriptId: string) => Promise<void>;
  setCurrentStep: (stepNumber: number) => void;
  updateStepData: (stepNumber: number, data: Partial<StepData>) => void;
  saveStepData: (stepNumber: number) => Promise<void>;
  markStepComplete: (stepNumber: number) => void;
  markStepNeedsReview: (stepNumber: number) => void;
  markStepInProgress: (stepNumber: number) => void;
  getStepData: (stepNumber: number) => StepData | undefined;
  getStepStatus: (stepNumber: number) => StepStatus;
  getCompletedStepsCount: () => number;
  isAllStepsCompleted: () => boolean;
  clearScript: () => void;
  refreshScript: () => Promise<void>;

  // 本地备份
  backupToLocal: (stepNumber: number, data: Partial<StepData>) => void;
  restoreFromLocal: (stepNumber: number) => Partial<StepData> | null;
  clearLocalBackup: (stepNumber: number) => void;
}

/**
 * 从 EpisodeScript 提取步骤数据
 */
function extractStepData(script: IEpisodeScript, stepNumber: number): StepData | undefined {
  switch (stepNumber) {
    case 1:
      return {
        title: script.title || '',
        summary: script.summary || '',
        highlights: script.highlights || [],
        language: script.language || 'zh-CN'
      } as StorySummaryData;
    case 2:
      return {
        styleName: script.styleName || '',
        artStyle: script.artStyle || '',
        basePrompt: script.basePrompt || '',
        bgmId: script.bgmId,
        bgmUrl: script.bgmUrl,
        narratorVoiceUrl: script.narratorVoiceUrl
      } as VisualStyleData;
    case 3:
      return {
        characters: script.characters || []
      } as CharactersData;
    case 4:
      return {
        props: script.props || []
      } as PropsData;
    case 5:
      return {
        scenes: script.scenes || []
      } as ScenesData;
    case 6:
      return {
        shots: script.shots || []
      } as ShotsData;
    default:
      return undefined;
  }
}

/**
 * 初始化步骤状态
 */
function initializeSteps(script: IEpisodeScript): ScriptStep[] {
  const { generationStatus, currentGenerationStep, generationStepStatus } = script;
  const isProcessing = generationStatus === ScriptGenerationStatus.PROCESSING;
  const isCompleted = generationStatus === ScriptGenerationStatus.COMPLETED;
  const isIdle = generationStatus === ScriptGenerationStatus.IDLE;

  // 优先使用 generationStepStatus（后端实时更新的字段）
  if (generationStepStatus && generationStepStatus.length > 0) {
    return SCRIPT_STEPS.map(({ stepNumber, name }) => {
      const data = extractStepData(script, stepNumber);
      const stepStatus = generationStepStatus.find(s => s.step === stepNumber);

      let status = StepStatus.PENDING;
      if (stepStatus) {
        switch (stepStatus.status) {
          case 'completed':
            status = StepStatus.COMPLETED;
            break;
          case 'processing':
            status = StepStatus.IN_PROGRESS;
            break;
          case 'failed':
            status = StepStatus.PENDING; // 失败的步骤显示为待处理，让用户可以重试
            break;
          default:
            status = StepStatus.PENDING;
        }
      } else if (hasStepData(data, stepNumber)) {
        // 如果没有步骤状态但有数据，标记为完成
        status = StepStatus.COMPLETED;
      }

      return {
        stepNumber,
        name,
        status,
        data
      };
    });
  }

  // 如果脚本已有 steps 数据，使用它（兼容旧数据）
  if (script.steps && script.steps.length === 6) {
    return script.steps;
  }

  // 根据脚本数据和生成状态推断步骤状态
  return SCRIPT_STEPS.map(({ stepNumber, name }) => {
    const data = extractStepData(script, stepNumber);
    let status = StepStatus.PENDING;

    if (isIdle) {
      // IDLE 状态：所有步骤都是待处理（生成还没开始）
      status = StepStatus.PENDING;
    } else if (isProcessing) {
      // PROCESSING 状态：根据 currentGenerationStep 判断
      if (stepNumber < currentGenerationStep) {
        // 小于当前步骤的，如果有数据则标记为完成
        status = hasStepData(data, stepNumber) ? StepStatus.COMPLETED : StepStatus.PENDING;
      } else if (stepNumber === currentGenerationStep) {
        // 当前正在生成的步骤
        status = StepStatus.IN_PROGRESS;
      } else {
        // 大于当前步骤的，保持待处理
        status = StepStatus.PENDING;
      }
    } else if (isCompleted) {
      // COMPLETED 状态：根据数据是否存在判断
      if (hasStepData(data, stepNumber)) {
        status = StepStatus.COMPLETED;
      }
    } else {
      // FAILED 或其他状态：根据数据和 currentGenerationStep 判断
      if (stepNumber < currentGenerationStep && hasStepData(data, stepNumber)) {
        status = StepStatus.COMPLETED;
      } else if (stepNumber === currentGenerationStep) {
        // 失败的步骤
        status = StepStatus.PENDING;
      }
    }

    return {
      stepNumber,
      name,
      status,
      data
    };
  });
}

/**
 * 检查步骤是否有有效数据
 */
function hasStepData(data: StepData | undefined, stepNumber: number): boolean {
  if (!data) return false;

  switch (stepNumber) {
    case 1:
      const summaryData = data as StorySummaryData;
      return !!(summaryData.title || summaryData.summary);
    case 2:
      const styleData = data as VisualStyleData;
      return !!(styleData.styleName || styleData.artStyle);
    case 3:
      const charData = data as CharactersData;
      return !!(charData.characters && charData.characters.length > 0);
    case 4:
      const propsData = data as PropsData;
      return !!(propsData.props && propsData.props.length > 0);
    case 5:
      const scenesData = data as ScenesData;
      return !!(scenesData.scenes && scenesData.scenes.length > 0);
    case 6:
      const shotsData = data as ShotsData;
      return !!(shotsData.shots && shotsData.shots.length > 0);
    default:
      return false;
  }
}

/**
 * 脚本生成 Store
 */
export const useScriptGenerationStore = create<ScriptGenerationState>()(
  persist(
    (set, get) => ({
      // 初始状态
      script: null,
      scriptId: null,
      loading: false,
      error: null,
      currentStep: 1,
      steps: [],
      isDirty: false,
      pendingChanges: null,
      lastSavedAt: null,
      localBackups: [],

      // 加载脚本
      loadScript: async (scriptId: string) => {
        const state = get();

        // 如果已经加载了相同的脚本，不重复加载
        if (state.scriptId === scriptId && state.script) {
          return;
        }

        set({ loading: true, error: null, scriptId });

        try {
          const script = await episodeScriptApi.getEpisodeScript(scriptId);
          const steps = initializeSteps(script);

          set({
            script,
            steps,
            loading: false,
            currentStep: 1,
            isDirty: false,
            pendingChanges: null
          });
        } catch (error: any) {
          console.error('加载脚本失败:', error);
          set({
            error: error.message || '加载脚本失败',
            loading: false
          });
        }
      },

      // 设置当前步骤
      setCurrentStep: (stepNumber: number) => {
        if (stepNumber >= 1 && stepNumber <= 6) {
          set({ currentStep: stepNumber });
        }
      },

      // 更新步骤数据（本地）
      updateStepData: (stepNumber: number, data: Partial<StepData>) => {
        const { steps } = get();
        const updatedSteps = steps.map(step => {
          if (step.stepNumber === stepNumber) {
            return {
              ...step,
              data: { ...step.data, ...data } as StepData
            };
          }
          return step;
        });

        set({
          steps: updatedSteps,
          isDirty: true,
          pendingChanges: data
        });

        // 自动备份到本地存储
        get().backupToLocal(stepNumber, data);
      },

      // 保存步骤数据到服务器
      saveStepData: async (stepNumber: number) => {
        const { script, steps } = get();
        if (!script) return;

        const step = steps.find(s => s.stepNumber === stepNumber);
        if (!step || !step.data) return;

        try {
          // 根据步骤类型构建更新数据
          let updates: Partial<IEpisodeScript> = {};

          switch (stepNumber) {
            case 1:
              const summaryData = step.data as StorySummaryData;
              updates = {
                title: summaryData.title,
                summary: summaryData.summary,
                highlights: summaryData.highlights,
                language: summaryData.language
              };
              break;
            case 2:
              const styleData = step.data as VisualStyleData;
              updates = {
                styleName: styleData.styleName,
                artStyle: styleData.artStyle,
                basePrompt: styleData.basePrompt,
                bgmId: styleData.bgmId,
                bgmUrl: styleData.bgmUrl,
                narratorVoiceUrl: styleData.narratorVoiceUrl
              };
              break;
            case 3:
              const charData = step.data as CharactersData;
              updates = { characters: charData.characters };
              break;
            case 4:
              const propsData = step.data as PropsData;
              updates = { props: propsData.props };
              break;
            case 5:
              const scenesData = step.data as ScenesData;
              updates = { scenes: scenesData.scenes };
              break;
            case 6:
              const shotsData = step.data as ShotsData;
              updates = { shots: shotsData.shots };
              break;
          }

          await episodeScriptApi.updateEpisodeScript(script.id, updates);

          set({
            isDirty: false,
            pendingChanges: null,
            lastSavedAt: Date.now()
          });

          // 清除本地备份
          get().clearLocalBackup(stepNumber);
        } catch (error: any) {
          console.error('保存步骤数据失败:', error);
          throw error;
        }
      },

      // 标记步骤完成
      markStepComplete: (stepNumber: number) => {
        const { steps } = get();
        const updatedSteps = steps.map(step => {
          if (step.stepNumber === stepNumber) {
            return {
              ...step,
              status: StepStatus.COMPLETED,
              completedAt: new Date()
            };
          }
          return step;
        });
        set({ steps: updatedSteps });
      },

      // 标记步骤需要审查
      markStepNeedsReview: (stepNumber: number) => {
        const { steps } = get();
        const updatedSteps = steps.map(step => {
          if (step.stepNumber === stepNumber) {
            return {
              ...step,
              status: StepStatus.NEEDS_REVIEW
            };
          }
          return step;
        });
        set({ steps: updatedSteps });
      },

      // 标记步骤正在生成中
      markStepInProgress: (stepNumber: number) => {
        const { steps } = get();
        const updatedSteps = steps.map(step => {
          if (step.stepNumber === stepNumber) {
            return {
              ...step,
              status: StepStatus.IN_PROGRESS
            };
          }
          return step;
        });
        set({ steps: updatedSteps });
      },

      // 获取步骤数据
      getStepData: (stepNumber: number) => {
        const { steps, script } = get();
        const step = steps.find(s => s.stepNumber === stepNumber);
        if (step?.data) return step.data;
        if (script) return extractStepData(script, stepNumber);
        return undefined;
      },

      // 获取步骤状态
      getStepStatus: (stepNumber: number) => {
        const { steps } = get();
        const step = steps.find(s => s.stepNumber === stepNumber);
        return step?.status || StepStatus.PENDING;
      },

      // 获取已完成步骤数量
      getCompletedStepsCount: () => {
        const { steps } = get();
        return steps.filter(s => s.status === StepStatus.COMPLETED).length;
      },

      // 检查是否所有步骤都已完成
      isAllStepsCompleted: () => {
        const { steps } = get();
        return steps.every(s => s.status === StepStatus.COMPLETED);
      },

      // 清除脚本
      clearScript: () => {
        set({
          script: null,
          scriptId: null,
          loading: false,
          error: null,
          currentStep: 1,
          steps: [],
          isDirty: false,
          pendingChanges: null,
          lastSavedAt: null
        });
      },

      // 刷新脚本
      refreshScript: async () => {
        const { scriptId, steps: currentSteps } = get();
        if (!scriptId) return;

        try {
          const script = await episodeScriptApi.getEpisodeScript(scriptId);
          const newSteps = initializeSteps(script);

          // 智能合并步骤状态：
          // 1. 如果当前步骤是 IN_PROGRESS，保持 IN_PROGRESS（除非新数据显示已完成）
          // 2. 如果当前步骤是 COMPLETED，保持 COMPLETED（除非新数据也显示已完成，则使用新数据）
          // 3. 如果新数据显示 COMPLETED，使用新数据
          const mergedSteps = newSteps.map((newStep, index) => {
            const currentStep = currentSteps[index];
            if (!currentStep) return newStep;

            // 如果新状态是 COMPLETED，使用新状态
            if (newStep.status === StepStatus.COMPLETED) {
              return newStep;
            }

            // 如果当前状态是 IN_PROGRESS 或 COMPLETED，保持当前状态
            if (currentStep.status === StepStatus.IN_PROGRESS ||
              currentStep.status === StepStatus.COMPLETED) {
              return {
                ...newStep,
                status: currentStep.status
              };
            }

            return newStep;
          });

          set({ script, steps: mergedSteps });
        } catch (error: any) {
          console.error('刷新脚本失败:', error);
        }
      },

      // 备份到本地存储
      backupToLocal: (stepNumber: number, data: Partial<StepData>) => {
        const { scriptId, localBackups } = get();
        if (!scriptId) return;

        const backup: LocalBackup = {
          scriptId,
          stepNumber,
          data,
          timestamp: Date.now()
        };

        // 更新或添加备份
        const existingIndex = localBackups.findIndex(
          b => b.scriptId === scriptId && b.stepNumber === stepNumber
        );

        let updatedBackups: LocalBackup[];
        if (existingIndex >= 0) {
          updatedBackups = [...localBackups];
          updatedBackups[existingIndex] = backup;
        } else {
          updatedBackups = [...localBackups, backup];
        }

        set({ localBackups: updatedBackups });
      },

      // 从本地存储恢复
      restoreFromLocal: (stepNumber: number) => {
        const { scriptId, localBackups } = get();
        if (!scriptId) return null;

        const backup = localBackups.find(
          b => b.scriptId === scriptId && b.stepNumber === stepNumber
        );

        return backup?.data || null;
      },

      // 清除本地备份
      clearLocalBackup: (stepNumber: number) => {
        const { scriptId, localBackups } = get();
        if (!scriptId) return;

        const updatedBackups = localBackups.filter(
          b => !(b.scriptId === scriptId && b.stepNumber === stepNumber)
        );

        set({ localBackups: updatedBackups });
      }
    }),
    {
      name: 'script-generation-storage',
      partialize: (state) => ({
        localBackups: state.localBackups
      })
    }
  )
);
